import { firstLetterToUppercase, isEmpty, isNull, isType, isUndefined } from '../helpers';
import rules from './Rules/list';
import __ from '../locales/__';
/** GLOBAL METHODS */
const pushIfDontExist = (array, element) => {
    if (array.indexOf(element) === -1)
        array.push(element);
};
const verifyTypeToPush = (array, elements) => {
    Array.isArray(elements)
        ? elements.forEach(element => pushIfDontExist(array, element))
        : pushIfDontExist(array, elements);
};
/**
 * Adds elements to the specified array at the correct position.
 * If an index is provided, the elements are added to a nested array at that index.
 * If no index is provided, the elements are added directly to the array.
 *
 * @param array - The array to which elements will be added.
 * @param elements - The elements to add to the array.
 * @param index - The index at which to add the elements (optional, defaults to -1).
 */
export const pushAtTheRightPlace = (array, elements, index = -1) => {
    if (index !== -1) {
        /* If index is specified, add in recursive array */
        if (!Array.isArray(array[index]))
            array[index] = [];
        if (!isUndefined(elements))
            verifyTypeToPush(array[index], elements);
    }
    else
        verifyTypeToPush(array, elements);
};
export const ruleExist = rule => {
    if (isUndefined(rules[rule]))
        throw new Error(`the rule "${rule}" does not exist`);
};
class ErrorForm {
    parent;
    attributes;
    // @ts-ignore
    missing;
    // @ts-ignore
    inputs;
    // @ts-ignore
    list;
    // @ts-ignore
    missings;
    constructor(parent, attributes) {
        this.parent = parent;
        this.attributes = attributes;
        this.reset();
    }
    get any() {
        return Array.isArray(this.inputs[0])
            ? this.inputs.map(input => input.length !== 0)
            : this.inputs.length !== 0;
    }
    get hasMissing() {
        return Array.isArray(this.missings[0])
            ? this.missings.map(missing => missing.length !== 0)
            : this.missings.length !== 0;
    }
    get translatedInputs() {
        return this.inputs.map(element => Array.isArray(element)
            ? element.map(error => this.#chooseTradAttribute(error))
            : this.#chooseTradAttribute(element));
    }
    get translatedMissing() {
        return this.missings.map(element => Array.isArray(element)
            ? element.map(missing => this.#chooseTradAttribute(missing))
            : this.#chooseTradAttribute(element));
    }
    static #tradAttribute(attribute) {
        return firstLetterToUppercase(__('attributes.' + attribute));
    }
    reset() {
        /* To show errors in list */
        this.list = [];
        /* To know which inputs have an error */
        this.inputs = [];
        /* To know which inputs are missing */
        this.missings = [];
        if (Array.isArray(this.parent.data)) {
            this.parent.data.forEach(_ => {
                // @ts-ignore
                this.inputs.push([]);
                // @ts-ignore
                this.list.push([]);
                // @ts-ignore
                this.missings.push([]);
            });
        }
    }
    has(field, index = -1) {
        return index === -1
            ? this.inputs.includes(field)
            : this.inputs[index].includes(field);
    }
    push(element, property, index) {
        pushAtTheRightPlace(element, property, index);
    }
    pushMissing(property, index) {
        this.push(this.missings, property, index);
    }
    pushError(property, index) {
        this.push(this.inputs, property, index);
    }
    #chooseTradAttribute(property) {
        return ErrorForm.#tradAttribute(this.attributes[property] || property);
    }
}
export default class Validator {
    data;
    rules;
    messages;
    custom;
    error;
    constructor({ data, rules, messages = {}, customData = {}, attributes = {} }) {
        this.data = data;
        this.rules = rules;
        this.isRulesTypeOk();
        this.messages = messages;
        this.error = new ErrorForm(this, attributes);
        this.custom = customData;
    }
    get dataType() {
        if (Array.isArray(this.data))
            return 'array';
        else if (isType(this.data, 'object'))
            return 'object';
        else
            throw new Error(`the data should be an array or an object`);
    }
    get valid() {
        if (Array.isArray(this.error.any)) {
            return this.error.any.map((any, index) => !any && !this.error.hasMissing[index]);
        }
        else
            return !this.error.any && !this.error.hasMissing;
    }
    get fails() {
        return Array.isArray(this.valid)
            ? this.valid.some(error => !error)
            : !this.valid;
    }
    static #rulesConverted(rule) {
        return isType(rule, 'string')
            ? rule.split('|')
            : rule;
    }
    isRulesTypeOk() {
        if (!isType(this.rules, 'object'))
            throw new Error(`the rules should be an object`);
    }
    validate() {
        this.errors().reset();
        this.#chooseValidationProcess();
        return this;
    }
    errors() {
        return this.error;
    }
    #pushErrors(rule, property, index) {
        this.errors().pushError(property, index);
        /* If the Message is not defined, it means that we don't add message to the list of error message, we will only add the input error in the inputs array */
        if (rule.message() !== rule.ruleName)
            pushAtTheRightPlace(this.errors().list, rule.message(), index);
    }
    #ruleFunctionActions(pass, errors, elementToValidate, property, index, hasMissing) {
        if (!pass) {
            /** If has missing : it means we used a validator */
            if (hasMissing)
                this.errors().pushMissing(property, index);
            if (!isEmpty(elementToValidate) && !isEmpty(errors)) {
                this.errors().pushError(property, index);
                pushAtTheRightPlace(this.errors().list, errors, index);
            }
        }
    }
    #ruleClassicActions(ruleName, elementToValidate, index, property, parameters) {
        ruleExist(ruleName);
        const rule = new rules[ruleName]({
            value: elementToValidate,
            customAttribute: this.errors().attributes[property] || property,
            data: this.#currentData(index),
            parameters
        });
        if (!rule.passes()) {
            if (isEmpty(elementToValidate)) {
                if (['required', 'requiredUnless', 'RequiredWith'].includes(ruleName))
                    this.errors().pushMissing(property, index);
            }
            else
                this.#pushErrors(rule, property, index);
        }
    }
    #applyClassicRule(fullRule, elementToValidate, property, index) {
        if (/:/.test(fullRule)) {
            const [rule, parameters] = fullRule.split(':');
            return this.#ruleClassicActions(rule, elementToValidate, index, property, parameters);
        }
        else {
            return this.#ruleClassicActions(fullRule, elementToValidate, index, property);
        }
    }
    /**
     * Applies a validation rule to a specific element.
     *
     * @param elementToValidate - The data element to validate.
     * @param rule - The validation rule, which can be a function or a string.
     * @param property - The property name of the data being validated.
     * @param index - The index of the data in case of an array, or -1 for objects.
     */
    #applyRule(elementToValidate, rule, property, index) {
        if (isType(rule, 'function')) {
            /* Pass all the data so we can use it in our function
            When index is defined pass the data of the current element
            * */
            const customRule = (pass, errors = [], hasMissing) => {
                this.#ruleFunctionActions(pass, errors, elementToValidate, property, index, hasMissing);
            };
            rule(customRule, this.#currentData(index), this.custom);
        }
        else
            this.#applyClassicRule(rule, elementToValidate, property, index);
    }
    #getNestedData(data, property) {
        const [attribute, ...rest] = property;
        return rest.length === 0
            ? (isNull(data) || isUndefined(data) ? data : data[attribute])
            : this.#getNestedData(data[attribute], rest);
    }
    #currentData(index, property) {
        let data = index === -1
            ? this.data
            : this.data[index];
        return isUndefined(property)
            ? data
            // @ts-ignore
            : this.#getNestedData(data, property.split('.'));
    }
    #applyRulesOnData(property, index = -1) {
        Validator.#rulesConverted(this.rules[property]).forEach(rule => {
            this.#applyRule(this.#currentData(index, property), rule, property, index);
        });
    }
    #validateArray(property) {
        if (Array.isArray(this.data))
            this.data.forEach((data, index) => this.#applyRulesOnData(property, index));
    }
    #chooseValidationProcess() {
        Object.entries(this.rules).forEach(([property]) => {
            switch (this.dataType) {
                case 'array':
                    this.#validateArray(property);
                    break;
                case 'object':
                    this.#applyRulesOnData(property);
                    break;
            }
        });
    }
}
